// Navigation: 1 m walkability grid + A* + string-pull smoothing (plan §7.2, budgeted per R5).
//
// Castle gates have two navigation views. Immediate walkability is physical and shared:
// a closed gate blocks everyone and an open gate admits everyone. Route planning may cross
// the owner's own closed auto-gate so friendly traffic advances to its trigger instead of
// deciding the far side is unreachable. Opponents still treat a closed gate as a barrier.

import type { MapDef, PlayerId, TeamId } from '../content/types';
import type { Vec2 } from '../shared/vec';
import { clamp } from '../shared/vec';

/** Squad formation clearance baked into rocks so visible members stay off their slopes. // TEST */
const ROCK_CLEARANCE = 1.5;

/**
 * Exact supercover segment test: visits, in order, every grid cell the segment touches.
 * Crossing exactly through a cell corner additionally requires both orthogonal neighbors
 * open (A*'s no-corner-cutting rule). This replaces fixed-interval point sampling, which
 * could skip the sliver of a blocked cell that per-tick physical movement then landed in —
 * the cause of units wedging permanently against building/rock footprint corners.
 */
export function segmentClear(
  a: Vec2,
  b: Vec2,
  open: (cx: number, cy: number) => boolean,
): boolean {
  let cx = Math.floor(a.x);
  let cy = Math.floor(a.y);
  if (!open(cx, cy)) return false;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const stepX = dx > 0 ? 1 : -1;
  const stepY = dy > 0 ? 1 : -1;
  const tDeltaX = dx === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = dy === 0 ? Infinity : Math.abs(1 / dy);
  // Parametric t (0..1 along a→b) of the next vertical / horizontal grid-line crossing.
  let tMaxX = dx === 0 ? Infinity : (dx > 0 ? cx + 1 - a.x : a.x - cx) * tDeltaX;
  let tMaxY = dy === 0 ? Infinity : (dy > 0 ? cy + 1 - a.y : a.y - cy) * tDeltaY;
  while (Math.min(tMaxX, tMaxY) <= 1) {
    if (tMaxX === tMaxY) {
      // exact corner crossing — both side cells must be open, then continue diagonally
      if (!open(cx + stepX, cy) || !open(cx, cy + stepY)) return false;
      cx += stepX;
      cy += stepY;
      tMaxX += tDeltaX;
      tMaxY += tDeltaY;
    } else if (tMaxX < tMaxY) {
      cx += stepX;
      tMaxX += tDeltaX;
    } else {
      cy += stepY;
      tMaxY += tDeltaY;
    }
    if (!open(cx, cy)) return false;
  }
  return true;
}

export class NavGrid {
  readonly w: number;
  readonly h: number;
  /** 0 = walkable, >0 = blocked (count of blockers so buildings can unstamp) */
  private cells: Uint8Array;
  /** Immutable terrain-only snapshot (rocks now; future lava/terrain blockers). */
  private terrainCells: Uint8Array;
  /** closedGateFor[p][i] > 0 — a closed gate owned by p physically occupies cell i */
  private closedGateFor: Uint8Array[];
  /** Invalidates cached connected-component labels whenever an obstacle overlay changes. */
  private connectivityRevision = 0;
  private connectivityCache: Array<{ revision: number; labels: Int32Array } | null> = [null, null, null, null, null];
  private terrainConnectivity: Int32Array | null = null;
  private readonly playerTeams: TeamId[];

  constructor(map: MapDef, playerTeams: TeamId[]) {
    this.w = map.width;
    this.h = map.height;
    this.cells = new Uint8Array(this.w * this.h);
    this.closedGateFor = Array.from({ length: 4 }, () => new Uint8Array(this.w * this.h));
    this.playerTeams = playerTeams;
    for (const r of map.rocks) {
      // rocks block an inscribed ellipse, not the full rect — rounded ends stop squads
      // from wedging on square corners in the chokes. The ellipse is inflated by a squad
      // clearance margin (obstacle-inflation): paths keep ~1.5 m off the real rock, so squads
      // route AROUND corners instead of scraping and wedging on them (owner feedback).
      this.stampEllipse(r.pos.x, r.pos.y, r.w / 2 + ROCK_CLEARANCE, r.h / 2 + ROCK_CLEARANCE, 1);
    }
    this.terrainCells = this.cells.slice();
  }

  get revision(): number {
    return this.connectivityRevision;
  }

  idx(cx: number, cy: number): number {
    return cy * this.w + cx;
  }

  inBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.w && cy < this.h;
  }

  /** Current physical walkability. `forOwner` remains for call-site compatibility. */
  isWalkableCell(cx: number, cy: number, _forOwner?: PlayerId): boolean {
    if (!this.inBounds(cx, cy)) return false;
    const i = this.idx(cx, cy);
    if (this.cells[i] !== 0) return false;
    return this.closedGateFor.every((overlay) => overlay[i] === 0);
  }

  isWalkable(p: Vec2, forOwner?: PlayerId): boolean {
    return this.isWalkableCell(Math.floor(p.x), Math.floor(p.y), forOwner);
  }

  isTerrainWalkable(p: Vec2): boolean {
    return this.isTerrainWalkableCell(Math.floor(p.x), Math.floor(p.y));
  }

  isTerrainWalkableCell(cx: number, cy: number): boolean {
    return this.inBounds(cx, cy) && this.terrainCells[this.idx(cx, cy)] === 0;
  }

  /** Owner-aware route planning: a friendly auto-gate may be planned through while shut. */
  private isPathableCell(cx: number, cy: number, forOwner?: PlayerId): boolean {
    if (!this.inBounds(cx, cy)) return false;
    const i = this.idx(cx, cy);
    if (this.cells[i] !== 0) return false;
    if (forOwner === undefined) {
      return this.closedGateFor.every((overlay) => overlay[i] === 0);
    }
    const team = this.playerTeams[forOwner];
    return this.closedGateFor.every((overlay, slot) => this.playerTeams[slot] === team || overlay[i] === 0);
  }

  stampRect(x: number, y: number, w: number, h: number, delta: 1 | -1): void {
    const x0 = clamp(Math.floor(x), 0, this.w - 1);
    const y0 = clamp(Math.floor(y), 0, this.h - 1);
    const x1 = clamp(Math.ceil(x + w) - 1, 0, this.w - 1);
    const y1 = clamp(Math.ceil(y + h) - 1, 0, this.h - 1);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const i = this.idx(cx, cy);
        const v = this.cells[i] + delta;
        this.cells[i] = v < 0 ? 0 : v;
      }
    }
    this.connectivityRevision++;
  }

  /** Add/remove a physically closed gate aperture, refcounted by gate owner. */
  stampGate(x: number, y: number, w: number, h: number, gateOwner: PlayerId, delta: 1 | -1): void {
    const overlay = this.closedGateFor[gateOwner]!;
    const x0 = clamp(Math.floor(x), 0, this.w - 1);
    const y0 = clamp(Math.floor(y), 0, this.h - 1);
    const x1 = clamp(Math.ceil(x + w) - 1, 0, this.w - 1);
    const y1 = clamp(Math.ceil(y + h) - 1, 0, this.h - 1);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const i = this.idx(cx, cy);
        const v = overlay[i] + delta;
        overlay[i] = v < 0 ? 0 : v;
      }
    }
    this.connectivityRevision++;
  }

  /** Stamp the ellipse inscribed in the (cx±rx, cy±ry) box, by integer row spans. */
  stampEllipse(cx: number, cy: number, rx: number, ry: number, delta: 1 | -1): void {
    const y0 = clamp(Math.floor(cy - ry), 0, this.h - 1);
    const y1 = clamp(Math.ceil(cy + ry) - 1, 0, this.h - 1);
    for (let gy = y0; gy <= y1; gy++) {
      // sample the ellipse at the row's cell-center
      const ny = (gy + 0.5 - cy) / ry;
      const span = 1 - ny * ny;
      if (span <= 0) continue;
      const halfW = rx * Math.sqrt(span);
      const x0 = clamp(Math.floor(cx - halfW), 0, this.w - 1);
      const x1 = clamp(Math.ceil(cx + halfW) - 1, 0, this.w - 1);
      for (let gx = x0; gx <= x1; gx++) {
        const i = this.idx(gx, gy);
        const v = this.cells[i] + delta;
        this.cells[i] = v < 0 ? 0 : v;
      }
    }
    this.connectivityRevision++;
  }

  /**
   * O(1) reachability lookup after one O(map) component build per nav revision and owner.
   * This is deliberately separate from A*: command validation only needs to know whether
   * a route exists, and must not allocate a full path once per squad in a selected army.
   */
  canReach(start: Vec2, goal: Vec2, forOwner?: PlayerId): boolean {
    const s = this.nearestPathable(start, 12, forOwner);
    const g = this.nearestPathable(goal, 12, forOwner, s);
    const cacheIndex = forOwner ?? 2;
    let cache = this.connectivityCache[cacheIndex];
    if (!cache || cache.revision !== this.connectivityRevision) {
      cache = {
        revision: this.connectivityRevision,
        labels: this.buildConnectivityLabels(forOwner),
      };
      this.connectivityCache[cacheIndex] = cache;
    }
    const startLabel = cache.labels[this.idx(Math.floor(s.x), Math.floor(s.y))];
    const goalLabel = cache.labels[this.idx(Math.floor(g.x), Math.floor(g.y))];
    return startLabel !== 0 && startLabel === goalLabel;
  }

  /** Reachability ignoring buildings and gates; used to distinguish a breach from terrain. */
  canReachTerrain(start: Vec2, goal: Vec2): boolean {
    const s = this.nearestTerrainWalkable(start);
    const g = this.nearestTerrainWalkable(goal);
    if (!this.terrainConnectivity) this.terrainConnectivity = this.buildTerrainConnectivityLabels();
    const startLabel = this.terrainConnectivity[this.idx(Math.floor(s.x), Math.floor(s.y))];
    const goalLabel = this.terrainConnectivity[this.idx(Math.floor(g.x), Math.floor(g.y))];
    return startLabel !== 0 && startLabel === goalLabel;
  }

  private nearestTerrainWalkable(p: Vec2, maxR = 12): Vec2 {
    const cx = clamp(Math.floor(p.x), 0, this.w - 1);
    const cy = clamp(Math.floor(p.y), 0, this.h - 1);
    const open = (x: number, y: number): boolean =>
      this.inBounds(x, y) && this.terrainCells[this.idx(x, y)] === 0;
    if (open(cx, cy)) return { x: cx + 0.5, y: cy + 0.5 };
    for (let r = 1; r <= maxR; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r || !open(cx + dx, cy + dy)) continue;
          return { x: cx + dx + 0.5, y: cy + dy + 0.5 };
        }
      }
    }
    return { x: cx + 0.5, y: cy + 0.5 };
  }

  private buildTerrainConnectivityLabels(): Int32Array {
    const size = this.w * this.h;
    const labels = new Int32Array(size);
    const queue = new Int32Array(size);
    const directions = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [1, -1], [-1, 1], [-1, -1],
    ] as const;
    const open = (x: number, y: number): boolean =>
      this.inBounds(x, y) && this.terrainCells[this.idx(x, y)] === 0;
    let component = 0;
    for (let start = 0; start < size; start++) {
      if (labels[start] !== 0) continue;
      const sx = start % this.w;
      const sy = (start / this.w) | 0;
      if (!open(sx, sy)) continue;
      component++;
      let head = 0;
      let tail = 0;
      labels[start] = component;
      queue[tail++] = start;
      while (head < tail) {
        const current = queue[head++];
        const cx = current % this.w;
        const cy = (current / this.w) | 0;
        for (const [dx, dy] of directions) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (!open(nx, ny)) continue;
          if (dx !== 0 && dy !== 0 && (!open(cx + dx, cy) || !open(cx, cy + dy))) continue;
          const next = this.idx(nx, ny);
          if (labels[next] !== 0) continue;
          labels[next] = component;
          queue[tail++] = next;
        }
      }
    }
    return labels;
  }

  private buildConnectivityLabels(forOwner?: PlayerId): Int32Array {
    const size = this.w * this.h;
    const labels = new Int32Array(size);
    const queue = new Int32Array(size);
    const directions = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const;
    let component = 0;

    for (let start = 0; start < size; start++) {
      if (labels[start] !== 0) continue;
      const sx = start % this.w;
      const sy = (start / this.w) | 0;
      if (!this.isPathableCell(sx, sy, forOwner)) continue;

      component++;
      let head = 0;
      let tail = 0;
      queue[tail++] = start;
      labels[start] = component;
      while (head < tail) {
        const current = queue[head++];
        const cx = current % this.w;
        const cy = (current / this.w) | 0;
        for (const [dx, dy] of directions) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (!this.isPathableCell(nx, ny, forOwner)) continue;
          if (
            dx !== 0 &&
            dy !== 0 &&
            (!this.isPathableCell(cx + dx, cy, forOwner) ||
              !this.isPathableCell(cx, cy + dy, forOwner))
          ) continue;
          const next = this.idx(nx, ny);
          if (labels[next] !== 0) continue;
          labels[next] = component;
          queue[tail++] = next;
        }
      }
    }
    return labels;
  }

  /**
   * Nearest walkable cell center to p (spiral search). When `preferFrom` is supplied,
   * equally near cells are chosen on that point's side instead of by scan order. This is
   * important for orders aimed at blocked building footprints: attackers approach the
   * near face rather than all routing to the top-left cell.
   */
  nearestWalkable(p: Vec2, maxR = 12, forOwner?: PlayerId, preferFrom?: Vec2): Vec2 {
    return this.nearestMatching(p, maxR, forOwner, preferFrom, false);
  }

  /** Planning counterpart: permits a point inside the owner's closed automatic gate. */
  private nearestPathable(p: Vec2, maxR = 12, forOwner?: PlayerId, preferFrom?: Vec2): Vec2 {
    return this.nearestMatching(p, maxR, forOwner, preferFrom, true);
  }

  private nearestMatching(
    p: Vec2,
    maxR: number,
    forOwner: PlayerId | undefined,
    preferFrom: Vec2 | undefined,
    planning: boolean,
  ): Vec2 {
    const cx = clamp(Math.floor(p.x), 0, this.w - 1);
    const cy = clamp(Math.floor(p.y), 0, this.h - 1);
    const accepts = (x: number, y: number): boolean =>
      planning ? this.isPathableCell(x, y, forOwner) : this.isWalkableCell(x, y);
    if (accepts(cx, cy)) return { x: cx + 0.5, y: cy + 0.5 };
    for (let r = 1; r <= maxR; r++) {
      let best: Vec2 | null = null;
      let bestD2 = Infinity;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          if (!accepts(cx + dx, cy + dy)) continue;
          const candidate = { x: cx + dx + 0.5, y: cy + dy + 0.5 };
          if (!preferFrom) return candidate;
          const pdx = candidate.x - preferFrom.x;
          const pdy = candidate.y - preferFrom.y;
          const d2 = pdx * pdx + pdy * pdy;
          if (d2 < bestD2) {
            best = candidate;
            bestD2 = d2;
          }
        }
      }
      if (best) return best;
    }
    return { x: cx + 0.5, y: cy + 0.5 };
  }

  /** Straight-line pathability check (exact supercover traversal, no sampling gaps). */
  lineOfSight(a: Vec2, b: Vec2, forOwner?: PlayerId): boolean {
    return segmentClear(a, b, (cx, cy) => this.isPathableCell(cx, cy, forOwner));
  }

  /**
   * A* on the grid, 8-directional without corner cutting; returns smoothed waypoints
   * (world coords) excluding the start. Callers issuing a movement order may opt into a
   * deterministic best-effort route so the unit advances to a wall/choke; reachability
   * probes keep the default exact-path semantics.
   */
  findPath(start: Vec2, goal: Vec2, forOwner?: PlayerId, allowPartial = false): Vec2[] | null {
    const s = this.nearestPathable(start, 12, forOwner);
    const g = this.nearestPathable(goal, 12, forOwner, s);
    const sx = Math.floor(s.x);
    const sy = Math.floor(s.y);
    const gx = Math.floor(g.x);
    const gy = Math.floor(g.y);
    if (sx === gx && sy === gy) return [g];

    const W = this.w;
    const size = this.w * this.h;
    const gScore = new Float32Array(size).fill(Infinity);
    const cameFrom = new Int32Array(size).fill(-1);
    const closed = new Uint8Array(size);
    // simple binary heap of [f, idx]
    const heap: number[] = [];
    const heapF: number[] = [];
    const push = (i: number, f: number) => {
      heap.push(i);
      heapF.push(f);
      let c = heap.length - 1;
      while (c > 0) {
        const p = (c - 1) >> 1;
        if (heapF[p] <= heapF[c]) break;
        [heap[p], heap[c]] = [heap[c], heap[p]];
        [heapF[p], heapF[c]] = [heapF[c], heapF[p]];
        c = p;
      }
    };
    const pop = (): number => {
      const top = heap[0];
      const last = heap.pop()!;
      const lastF = heapF.pop()!;
      if (heap.length > 0) {
        heap[0] = last;
        heapF[0] = lastF;
        let p = 0;
        for (;;) {
          const l = p * 2 + 1;
          const r = l + 1;
          let m = p;
          if (l < heap.length && heapF[l] < heapF[m]) m = l;
          if (r < heap.length && heapF[r] < heapF[m]) m = r;
          if (m === p) break;
          [heap[p], heap[m]] = [heap[m], heap[p]];
          [heapF[p], heapF[m]] = [heapF[m], heapF[p]];
          p = m;
        }
      }
      return top;
    };

    const h = (cx: number, cy: number) => {
      const dx = Math.abs(cx - gx);
      const dy = Math.abs(cy - gy);
      return Math.max(dx, dy) + 0.41421356 * Math.min(dx, dy);
    };

    const startIdx = sy * W + sx;
    const goalIdx = gy * W + gx;
    gScore[startIdx] = 0;
    push(startIdx, h(sx, sy));

    const DIRS = [
      [1, 0, 1],
      [-1, 0, 1],
      [0, 1, 1],
      [0, -1, 1],
      [1, 1, 1.41421356],
      [1, -1, 1.41421356],
      [-1, 1, 1.41421356],
      [-1, -1, 1.41421356],
    ] as const;

    let found = false;
    let bestIdx = startIdx;
    let bestH = h(sx, sy);
    let bestG = 0;
    let expansions = 0;
    const maxExpansions = 40000;
    while (heap.length > 0 && expansions < maxExpansions) {
      const cur = pop();
      if (closed[cur]) continue;
      const cx = cur % W;
      const cy = (cur / W) | 0;
      const curH = h(cx, cy);
      const curG = gScore[cur];
      if (
        curH < bestH - 1e-6 ||
        (Math.abs(curH - bestH) <= 1e-6 &&
          (curG < bestG - 1e-6 || (Math.abs(curG - bestG) <= 1e-6 && cur < bestIdx)))
      ) {
        bestIdx = cur;
        bestH = curH;
        bestG = curG;
      }
      if (cur === goalIdx) {
        found = true;
        break;
      }
      closed[cur] = 1;
      expansions++;
      for (const [dx, dy, cost] of DIRS) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (!this.isPathableCell(nx, ny, forOwner)) continue;
        // no corner cutting on diagonals
        if (
          dx !== 0 &&
          dy !== 0 &&
          (!this.isPathableCell(cx + dx, cy, forOwner) || !this.isPathableCell(cx, cy + dy, forOwner))
        )
          continue;
        const ni = ny * W + nx;
        if (closed[ni]) continue;
        const ng = gScore[cur] + cost;
        if (ng < gScore[ni]) {
          gScore[ni] = ng;
          cameFrom[ni] = cur;
          push(ni, ng + h(nx, ny));
        }
      }
    }
    if (!found && !allowPartial) return null;
    const endIdx = found ? goalIdx : bestIdx;
    if (endIdx === startIdx) return allowPartial ? [] : null;

    // reconstruct
    const raw: Vec2[] = [];
    let cur = endIdx;
    while (cur !== -1 && cur !== startIdx) {
      raw.push({ x: (cur % W) + 0.5, y: ((cur / W) | 0) + 0.5 });
      cur = cameFrom[cur];
    }
    raw.reverse();
    if (raw.length === 0) return [g];
    if (found) raw[raw.length - 1] = g;

    // string-pull smoothing — anchored at the TRUE start when its cell is pathable, so the
    // first smoothed segment is walkable from the unit's actual position. Anchoring at the
    // snapped cell center let the first segment clip a footprint corner that the unit,
    // standing up to ~0.7 m off-center, then physically hit and wedged on.
    const smoothed: Vec2[] = [];
    let anchor = this.isPathableCell(Math.floor(start.x), Math.floor(start.y), forOwner)
      ? start
      : s;
    let i = 0;
    while (i < raw.length) {
      // find furthest visible waypoint from anchor
      let far = i;
      for (let j = raw.length - 1; j > i; j--) {
        if (this.lineOfSight(anchor, raw[j], forOwner)) {
          far = j;
          break;
        }
      }
      smoothed.push(raw[far]);
      anchor = raw[far];
      i = far + 1;
    }
    return smoothed;
  }
}
